import { useEffect, useState } from 'react';
import { useJobProgress } from '../contexts/JobProgressContext';
import { X, CheckCircle2, XCircle } from 'lucide-react';

export default function ProgressModal({ jobId, jobType, onComplete, onClose }) {
  const { getJob, subscribeToJob } = useJobProgress();
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (jobId) {
      // Subscribe immediately
      subscribeToJob(jobId);
      
      // Check for job immediately
      const currentJob = getJob(jobId);
      if (currentJob) {
        setJob(currentJob);
      }
      
      // Set up polling interval - check frequently for smooth updates
      const interval = setInterval(() => {
        const currentJob = getJob(jobId);
        if (currentJob) {
          // Always update state to trigger re-render
          setJob({ ...currentJob }); // Create new object to force update
          
          // Auto-close on completion (only if onComplete is provided)
          if (currentJob.status === 'completed' && onComplete) {
            clearInterval(interval);
            // Call onComplete but don't auto-close - let user close manually
            setTimeout(() => {
              onComplete(currentJob);
            }, 2000);
          }
          
          // Auto-close on failure (only if onClose is provided)
          if (currentJob.status === 'failed' && onClose) {
            clearInterval(interval);
            setTimeout(() => {
              onClose();
            }, 5000); // Give more time to read error
          }
        }
      }, 200); // Check every 200ms for smooth progress
      
      return () => clearInterval(interval);
    }
  }, [jobId, subscribeToJob, getJob, onComplete, onClose]);

  if (!job && !jobId) return null;

  const currentJob = job || { status: 'running', progress: 0, messages: [], debugMessages: [] };
  const { status, progress, messages, duration, debugMessages = [] } = currentJob;
  const [showDebug, setShowDebug] = useState(true);

  const getJobTitle = () => {
    const type = jobType || currentJob.type || 'unknown';
    const titles = {
      'news-scraping': 'Scraping News Articles',
      'article-scoring': 'Scoring Article',
      'template-generation': 'Generating Email Template',
      'lead-scoring': 'Scoring Leads',
      'csv-import': 'Importing CSV',
      'campaign-execution': 'Executing Campaign',
      'mql-grading': 'Grading MQL'
    };
    return titles[type] || 'Processing...';
  };

  const getStatusColor = () => {
    if (status === 'completed') return 'bg-green-500';
    if (status === 'failed') return 'bg-red-500';
    return 'bg-blue-500';
  };

  const formatDuration = (ms) => {
    if (!ms) return '';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {getJobTitle()}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDebug(!showDebug)}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                title="Toggle Debug Output"
              >
                {showDebug ? 'Hide' : 'Show'} Debug
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4">
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className={`h-3 rounded-full transition-all duration-300 ${getStatusColor()}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>{progress}%</span>
            {duration && (
              <span>{formatDuration(duration)}</span>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`text-sm p-2 rounded ${
                  msg.type === 'error'
                    ? 'bg-red-50 text-red-800'
                    : msg.type === 'success'
                    ? 'bg-green-50 text-green-800'
                    : 'bg-gray-50 text-gray-700'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-xs text-gray-500 mt-0.5">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="flex-1">{msg.message}</span>
                </div>
              </div>
            ))}
            
            {/* Debug Messages */}
            {showDebug && debugMessages && debugMessages.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-500 mb-2">Debug Output:</div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {debugMessages.slice(-100).map((msg, idx) => (
                    <div
                      key={idx}
                      className="text-xs p-1.5 rounded bg-gray-900 text-green-400 font-mono"
                      style={{ fontFamily: 'monospace' }}
                    >
                      {msg}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          {status === 'completed' && (
            <div className="text-center flex items-center justify-center gap-2">
              <CheckCircle2 size={20} className="text-green-600" />
              <p className="text-green-600 font-medium">Completed successfully!</p>
            </div>
          )}
          {status === 'failed' && (
            <div className="text-center flex items-center justify-center gap-2">
              <XCircle size={20} className="text-red-600" />
              <div>
                <p className="text-red-600 font-medium">Failed</p>
                <p className="text-sm text-gray-600 mt-1">
                  {messages[messages.length - 1]?.message || 'Unknown error'}
                </p>
              </div>
            </div>
          )}
          {status === 'running' && (
            <div className="text-center">
              <p className="text-blue-600 text-sm">Processing... Please wait</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

